import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { IconAnalytics, IconChart, IconExpenses, IconInvoices } from '../lib/icons';
import { useTheme } from '../lib/ThemeContext';
import EmptyState from '../components/EmptyState';

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '\u00a50';
  return '\u00a5' + Number(amount).toLocaleString('ja-JP');
};

const formatShort = (amount) => {
  if (!amount && amount !== 0) return '\u00a50';
  const n = Number(amount);
  if (n >= 10000) {
    const man = n / 10000;
    return '\u00a5' + (man % 1 === 0 ? man : man.toFixed(1)) + '\u4e07';
  }
  return '\u00a5' + n.toLocaleString('ja-JP');
};

export default function Analytics() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [viewTab, setViewTab] = useState('monthly'); // 'monthly' | 'yearly'
  const [data, setData] = useState({ totalIncome:0, totalExpense:0, profit:0, tax:0, invoices:[], expenses:[], monthlyData:[], yearlyData:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const { data: _authData } = await supabase.auth.getUser(); const user = _authData?.user;
    if (!user) { setLoading(false); return; }

    const [invoiceRes, expenseRes] = await Promise.all([
      supabase.from('invoices').select('id, amount, status, issued_date').eq('user_id', user.id),
      supabase.from('expenses').select('*').eq('user_id', user.id),
    ]);

    const invoices = invoiceRes.data || [];
    const expenses = expenseRes.data || [];

    // issued_date から日付を取得するヘルパー
    const getInvoiceDate = (inv) => inv.issued_date || '';

    const totalIncome = invoices.filter(i => i.status === '入金済み').reduce((s,i) => s+(i.amount||0), 0);
    const totalExpense = expenses.reduce((s,e) => s+(e.amount||0), 0);
    const profit = totalIncome - totalExpense;
    const tax = Math.max(0, Math.floor(profit * 0.2));

    // ── 月別データ（過去6ヶ月）──
    const now = new Date();
    const monthlyData = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      const label = `${d.getMonth()+1}月`;
      const y = d.getFullYear();
      const m = String(d.getMonth()+1).padStart(2,'0');
      const prefix = `${y}-${m}`;
      const income = invoices
        .filter(inv => inv.status==='入金済み' && getInvoiceDate(inv).startsWith(prefix))
        .reduce((s,inv) => s+(inv.amount||0), 0);
      const expense = expenses
        .filter(exp => exp.date && exp.date.startsWith(prefix))
        .reduce((s,exp) => s+(exp.amount||0), 0);
      return { label, income, expense };
    });

    // ── 年別データ（過去3年）──
    const yearlyData = Array.from({ length: 3 }, (_, i) => {
      const yr = now.getFullYear() - (2 - i);
      const label = `${yr}年`;
      const income = invoices
        .filter(inv => inv.status==='入金済み' && getInvoiceDate(inv).startsWith(`${yr}`))
        .reduce((s,inv) => s+(inv.amount||0), 0);
      const expense = expenses
        .filter(exp => exp.date && exp.date.startsWith(`${yr}`))
        .reduce((s,exp) => s+(exp.amount||0), 0);
      return { label, income, expense };
    });

    setData({ totalIncome, totalExpense, profit, tax, invoices, expenses, monthlyData, yearlyData });
    setLoading(false);
  };

  const chartData = viewTab === 'monthly' ? data.monthlyData : data.yearlyData;
  const maxVal = Math.max(...chartData.map(m => Math.max(m.income, m.expense)), 1);

  // 現在表示中タブのサマリー（月別は今月、年別は今年）
  const now = new Date();
  const currentPeriodIncome = viewTab === 'monthly'
    ? (data.monthlyData[5]?.income || 0)   // 最新月（6番目）
    : (data.yearlyData[2]?.income  || 0);   // 最新年（3番目）
  const currentPeriodExpense = viewTab === 'monthly'
    ? (data.monthlyData[5]?.expense || 0)
    : (data.yearlyData[2]?.expense  || 0);

  const s = {
    card: { background:theme.bgCard, border:`1px solid ${theme.border}`, boxShadow:theme.shadow, borderRadius:14, padding:'16px' },
    label: { fontSize:11, color:theme.textSecondary, marginBottom:6 },
    tabBtn: (active) => ({
      flex: 1, padding: '9px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
      background: active ? theme.primary : theme.bgInput,
      color: active ? theme.primaryText : theme.textMuted,
      transition: 'all 0.2s',
    }),
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <p style={{ color:theme.textMuted }}>読み込み中...</p>
    </div>
  );

  const isEmpty = data.invoices.length === 0 && data.expenses.length === 0;

  return (
    <div style={{ padding:'20px 16px', maxWidth:720, margin:'0 auto' }}>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:20, fontWeight:700, color:theme.textPrimary, margin:0 }}><IconAnalytics size={18}/> 収支分析</h1>
        <p style={{ fontSize:13, color:theme.textMuted, margin:'4px 0 0' }}>累計データの分析</p>
      </div>

      {isEmpty ? (
        <EmptyState preset="analytics" onAction={() => navigate('/invoices')} />
      ) : (<>

      {/* 4つのサマリーカード（累計） */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'総収入（入金済）', value:data.totalIncome, color:theme.colorGreen },
          { label:'経費合計', value:data.totalExpense, color:theme.colorRed },
          { label:'利益', value:data.profit, color:theme.colorGreen2 },
          { label:'納税概算（20%）', value:data.tax, color:theme.colorAmber },
        ].map(item => (
          <div key={item.label} style={{ ...s.card, borderTop:`2px solid ${item.color}` }}>
            <div style={s.label}>{item.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:theme.textPrimary }}>{formatShort(item.value)}</div>
            <div style={{ fontSize:11, color:theme.textMuted, marginTop:4 }}>{formatCurrency(item.value)}</div>
          </div>
        ))}
      </div>

      {/* ── 月別 / 年別 タブ切り替え ── */}
      <div style={{ ...s.card, marginBottom:20 }}>
        {/* タブ */}
        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          <button style={s.tabBtn(viewTab === 'monthly')} onClick={() => setViewTab('monthly')}>月別</button>
          <button style={s.tabBtn(viewTab === 'yearly')}  onClick={() => setViewTab('yearly')}>年別</button>
        </div>

        <div style={{ fontSize:14, fontWeight:600, color:theme.textPrimary, marginBottom:4 }}>
          {viewTab === 'monthly' ? `月別収支（過去6ヶ月）` : `年別収支（過去3年）`}
        </div>

        {/* 当期サマリー小計 */}
        <div style={{ display:'flex', gap:16, marginBottom:16, flexWrap:'wrap' }}>
          <span style={{ fontSize:12, color:theme.textSecondary }}>
            {viewTab === 'monthly' ? `今月` : `今年`}の収入:
            <strong style={{ color:theme.colorGreen, marginLeft:4 }}>{formatShort(currentPeriodIncome)}</strong>
          </span>
          <span style={{ fontSize:12, color:theme.textSecondary }}>
            {viewTab === 'monthly' ? `今月` : `今年`}の経費:
            <strong style={{ color:theme.colorRed, marginLeft:4 }}>{formatShort(currentPeriodExpense)}</strong>
          </span>
          <span style={{ fontSize:12, color:theme.textSecondary }}>
            {viewTab === 'monthly' ? `今月` : `今年`}の利益:
            <strong style={{ color: currentPeriodIncome - currentPeriodExpense >= 0 ? '#34d399' : '#f87171', marginLeft:4 }}>
              {formatShort(currentPeriodIncome - currentPeriodExpense)}
            </strong>
          </span>
        </div>

        {/* グラフ */}
        <div style={{ display:'flex', gap:8, alignItems:'flex-end', height:160 }}>
          {chartData.map((m, i) => (
            <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
              <div style={{ width:'100%', display:'flex', gap:3, alignItems:'flex-end', height:130 }}>
                <div style={{ flex:1, background:theme.colorGreen, borderRadius:'4px 4px 0 0', height:`${(m.income/maxVal)*100}%`, minHeight: m.income>0?4:0 }} title={`収入: ${formatCurrency(m.income)}`} />
                <div style={{ flex:1, background:theme.colorRed, borderRadius:'4px 4px 0 0', height:`${(m.expense/maxVal)*100}%`, minHeight: m.expense>0?4:0 }} title={`経費: ${formatCurrency(m.expense)}`} />
              </div>
              <div style={{ fontSize:11, color:theme.textMuted }}>{m.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display:'flex', gap:16, marginTop:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}><div style={{ width:10, height:10, background:theme.colorGreen, borderRadius:2 }} /><span style={{ fontSize:11, color:theme.textSecondary }}>収入</span></div>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}><div style={{ width:10, height:10, background:theme.colorRed, borderRadius:2 }} /><span style={{ fontSize:11, color:theme.textSecondary }}>経費</span></div>
        </div>

        {/* テーブル表示（月別の詳細） */}
        <div style={{ marginTop:16, borderTop:`1px solid ${theme.border}`, paddingTop:12 }}>
          {chartData.map((m, i) => {
            const profit = m.income - m.expense;
            return (
              <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:`1px solid ${theme.border}` }}>
                <span style={{ fontSize:12, color:theme.textSecondary, minWidth:60 }}>{m.label}</span>
                <span style={{ fontSize:12, color:theme.colorGreen }}>{formatShort(m.income)}</span>
                <span style={{ fontSize:12, color:theme.colorRed }}>{formatShort(m.expense)}</span>
                <span style={{ fontSize:12, fontWeight:600, color: profit >= 0 ? '#34d399' : '#f87171' }}>{formatShort(profit)}</span>
              </div>
            );
          })}
          <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
            <span style={{ fontSize:10, color:theme.textMuted, minWidth:60 }}>期間</span>
            <span style={{ fontSize:10, color:theme.textMuted }}>収入</span>
            <span style={{ fontSize:10, color:theme.textMuted }}>経費</span>
            <span style={{ fontSize:10, color:theme.textMuted }}>利益</span>
          </div>
        </div>
      </div>

      {/* 最近の請求書 */}
      {data.invoices.length > 0 && (
        <div style={{ ...s.card, marginBottom:16 }}>
          <div style={{ fontSize:14, fontWeight:600, color:theme.textPrimary, marginBottom:12 }}><IconInvoices size={16}/> 請求書一覧</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {data.invoices.slice(0,5).map(inv => (
              <div key={inv.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:theme.bgInput, borderRadius:8 }}>
                <div>
                  <div style={{ fontSize:13, color:theme.textPrimary, fontWeight:500 }}>{inv.title || inv.invoice_no || '—'}</div>
                  <div style={{ fontSize:11, color:theme.textMuted }}>{inv.issued_date || '日付未設定'}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:theme.textPrimary }}>{formatCurrency(inv.amount)}</div>
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:20,
                    background: inv.status==='入金済み'?theme.badgeBg6: inv.status==='未入金'||inv.status==='入金待ち'?theme.badgeBg3:theme.badgeBg1,
                    color: inv.status==='入金済み'?theme.badgeTxt6: inv.status==='未入金'||inv.status==='入金待ち'?theme.badgeTxt3:theme.badgeTxt1
                  }}>{inv.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 経費一覧 */}
      {data.expenses.length > 0 && (
        <div style={s.card}>
          <div style={{ fontSize:14, fontWeight:600, color:theme.textPrimary, marginBottom:12 }}><IconExpenses size={16}/> 経費一覧</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {data.expenses.slice(0,5).map(exp => (
              <div key={exp.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', background:theme.bgInput, borderRadius:8 }}>
                <div>
                  <div style={{ fontSize:13, color:theme.textPrimary, fontWeight:500 }}>{exp.title}</div>
                  <div style={{ fontSize:11, color:theme.textMuted }}>{exp.category || 'その他'} · {exp.date || '—'}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:theme.dangerText }}>{formatCurrency(exp.amount)}</div>
                  {exp.tax_included !== undefined && (
                    <div style={{ fontSize:10, color:exp.tax_included ? '#86efac' : theme.textMuted }}>
                      {exp.tax_included ? '税込' : '税抜'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.invoices.length === 0 && data.expenses.length === 0 && (
        <div style={{ textAlign:'center', padding:'40px 0', color:theme.textMuted }}>
          <div style={{ fontSize:40, marginBottom:12 }}><IconChart size={40}/></div>
          <p style={{ fontSize:14 }}>請求書や経費を追加するとグラフが表示されます</p>
        </div>
      )}
      </>)}
    </div>
  );
}
